/**
 * Grid + wire models for the Corporate Actions Processing tab (PVAL-25952).
 *
 * The wire shapes mirror the contract shipped by the backend in PVAL-25926
 * (`helios-corporate-actions`):
 *
 * - `POST helios-corporate-actions/processing/commands` takes
 *   `{ rows: [...] }`; `accountId` is a JSON **number**, `exDate` a
 *   `YYYY-MM-DD` **string**, and the second-destination numerics are either
 *   omitted or valid non-null doubles — never `''` or `NaN`. Those fields are
 *   non-null strongly-typed Kotlin fields, so a single type mismatch in one
 *   cell fails Jackson deserialization for the WHOLE request and the response
 *   then carries no per-row `results` at all. All coercion therefore happens
 *   once, at request-build time (`util/corporate-actions-processing-request.util.ts`),
 *   never at cell-edit time.
 * - `rowId` is client-generated, opaque, and echoed back for UI mapping. It is
 *   the ONLY correlation key between a submitted row and its result, so it is
 *   also used as ag-grid's row node id (`getRowNodeId`). If the two ever drift,
 *   per-row messages get painted onto the wrong rows.
 */

/**
 * Command types this tab knows how to render. Only Cash / Stock Acquisition
 * ships in this cut; `GET processing/availableCommands` returns a plain JSON
 * array of these enum names and the registry is filtered down to it, so a user
 * without the CIAM policy for a command type never sees it in the dropdown.
 *
 * ## The VALUE is a wire contract — the member name is not
 *
 * Each value is copied VERBATIM from the backend `CommandTypes` Kotlin enum
 * (`libs/common-models/.../commands/CommandTypes.kt`), which carries no
 * `@JsonValue`/`@JsonProperty` override and therefore serializes as its literal
 * enum name. That single string is used in FOUR places:
 *
 * 1. the token `GET processing/availableCommands` returns (it responds with
 *    `List<CommandTypes>`), which `filterRegistryByAvailableCommands` matches on;
 * 2. the `commandType` discriminator on every POSTed row, resolved by Jackson via
 *    `@JsonSubTypes.Type(value = ProcessCashStockAcquisitionRow::class, name = ...)`;
 * 3. the backend's `SUPPORTED_COMMAND_TYPES` allow-list; and
 * 4. the CIAM resource id the Corporate Actions Processing policy is granted on.
 *
 * Shortening a value (e.g. dropping the `PROCESS_` prefix) is silently
 * catastrophic rather than loudly broken: the availableCommands filter matches
 * nothing, `activeEntry` is undefined and the grid never renders for ANY user —
 * and if a batch were somehow built, Jackson subtype resolution would reject the
 * whole request. Every future command type carries the same `PROCESS_` prefix, so
 * the "a new command type is one registry entry" promise depends on this
 * vocabulary being the backend enum name rather than a UI-local label.
 *
 * `corporate-action-processing-command-type.contract.spec.ts` pins the literals.
 * The TypeScript member names below are UI-local and free to change.
 */
export enum CorporateActionProcessingCommandType {
  CashStockAcquisition = 'PROCESS_CASH_STOCK_ACQUISITION',
}

/** Editable field keys shared by the grid row, the registry and the validator. */
export type ProcessingRowField =
  | 'accountId'
  | 'exDate'
  | 'targetCusip'
  | 'cashPerShare'
  | 'cashReceived'
  | 'destCusip'
  | 'destShareExchRate'
  | 'destCusipMarketPrice'
  | 'destCusip2'
  | 'destShareExchRate2'
  | 'destCusipMarketPrice2'
  | 'notes'

/**
 * Boolean per-row flags rendered as checkbox columns.
 *
 * Deliberately NOT members of `ProcessingRowField`, and this is load-bearing in
 * three separate ways:
 *
 * 1. `ProcessingGridRow extends Record<ProcessingRowField, string>`, but
 *    `EditableCheckboxRenderer.applyClick` calls `node.setDataValue` with a real
 *    `boolean`. Widening the string record would be a lie about the cell type.
 * 2. `PROCESSING_VALIDATED_FIELDS` is derived from the same vocabulary and drives
 *    `isRowEmpty`. A flag defaulting to `false` is a non-blank value, so a fresh
 *    untouched row would stop being "empty" — which breaks `activeRows`, the
 *    "untouched blank row is inactive" spec and the starter-row-replacement
 *    branch in `appendRows`.
 * 3. Excel paste maps positionally against the registry `columns` array. Keeping
 *    the flags in a separate `flagColumns` array leaves every existing paste
 *    index untouched.
 *
 * They also need no validation: both are `Boolean = false` on the shipped DTO.
 *
 * `forceRealizeAll` is a member on the REPORTER's explicit request in review on
 * MR !387 (perseus-worker's `ProcessCashStockAcquisitionCommand` declares it and
 * the corresponding stored proc defaults it to `false`). It was absent on an
 * earlier revision as unrequested editable financial scope; that sign-off has now
 * been given by the person who owns the requirement. See
 * `CASH_STOCK_ACQUISITION_FLAG_COLUMNS` for the full record.
 */
export type ProcessingFlagField =
  | 'ignoreCostBasisAdjustments'
  | 'forceRealizeAll'

/**
 * Row validity, matching the vocabulary `deduceValidityStatusCellClass`
 * (`@helios/create-transactions-modal`) understands.
 */
export type ProcessingValidityStatus =
  | 'inactive'
  | 'intermediate'
  | 'valid'
  | 'invalid'

/**
 * Submission-time outcome for a single row.
 *
 * `ACCEPTED` means the HeliosCommand was **queued**, not that the corporate
 * action was booked: perseus-worker's cash-reconciliation, security-eligibility,
 * open-lots and cost-basis checks run asynchronously and surface via the
 * existing job-failure toast + Jobs panel. Row/footer wording must never imply
 * success — see `describeSubmissionStatus` in the component.
 */
export const ROW_STATUS_ACCEPTED = 'ACCEPTED'

/**
 * The ONLY statuses that prove the server rejected a row without queueing
 * anything — an explicit allow-list, because everything else has to fail CLOSED.
 *
 * `status` is deliberately an open `string` (the backend may add a member to
 * `RowSubmissionStatus` at any time), so the handling has to pick a direction for
 * a status it does not recognise. There is exactly one safe direction: a row that
 * MIGHT be queued must be retired. Resolving an unknown status the other way —
 * "not ACCEPTED, therefore rejected, therefore still editable and still
 * submittable" — is how the duplicate-submission defect gets back in:
 * `{status:'QUEUED', commandId:'cmd-7'}` rendered red, the user edited a cell to
 * "fix" it, `revalidateRow` dropped the status AND `cmd-7`, and Submit re-POSTed
 * an already-queued financial corporate action that nothing catches
 * synchronously (perseus-worker's checks are async).
 *
 * So ONLY these literals route a row to the rejected-and-still-submittable
 * branch. Matching is exact and case-SENSITIVE: `'accepted'` and `'rejected'` are
 * not members, and both therefore retire the row. Adding a genuinely new
 * server-side rejection status here is a one-line change; forgetting to is
 * over-retirement (the user must delete the row and re-enter it), which is
 * recoverable — the opposite mistake duplicates a financial command.
 */
export const ROW_REJECTION_STATUSES: readonly string[] = [
  'REJECTED',
  'VALIDATION_FAILED',
  'FORBIDDEN',
  'ERROR',
]

/** True only for a status that PROVES nothing was queued for the row. */
export function isRejectionStatus(status?: string): boolean {
  return status !== undefined && ROW_REJECTION_STATUSES.includes(status)
}

/**
 * The two response-derived fields every retirement rule reads.
 *
 * `commandId` is part of the predicate, not just display data: its presence is
 * positive proof from the server that a HeliosCommand exists for the row.
 */
export interface ProcessingRowOutcome {
  submissionStatus?: string
  commandId?: string
}

/**
 * True once the row's command has been queued server-side.
 *
 * A queued row is RETIRED: it is excluded from any further batch, its cells are
 * no longer editable, and its `commandId` is never cleared. `submissionStatus`
 * is the single source of truth for that — there is deliberately no parallel
 * `submitted` boolean to drift out of step with it. Resubmitting a queued row
 * would duplicate a financial corporate-action command, and because
 * perseus-worker's checks are asynchronous nothing would catch the duplicate
 * synchronously.
 */
export function isRowAccepted(row?: ProcessingRowOutcome): boolean {
  return row?.submissionStatus === ROW_STATUS_ACCEPTED
}

/**
 * True when the row is proven to have become a HeliosCommand: ACCEPTED, or a
 * `commandId`, which is proof on its own. Narrower than `isRowRetired`, which
 * also covers UNCONFIRMED and unrecognised statuses.
 */
export function isRowQueued(row?: ProcessingRowOutcome): boolean {
  return isRowAccepted(row) || row?.commandId != null
}

/**
 * CLIENT-SIDE sentinel — never sent by the server.
 *
 * Marks a row that WAS transmitted in a batch but whose outcome the client
 * cannot determine, which happens on two paths:
 *
 * 1. the response came back but carried no result for this row (including a
 *    `200` with `results: []`), and
 * 2. the request itself failed AFTER transmission in a way that cannot
 *    distinguish "never reached the server" from "queued, then the connection
 *    dropped" — a timeout, a `504`, or any response-less error.
 *
 * The row is deliberately NOT marked accepted (it may not be queued) and NOT
 * left unmarked (an unmarked row is submittable, so the next Submit would
 * re-POST it and could duplicate a financial corporate-action command). It is
 * retired instead, and the footer tells the user to confirm via the Commands
 * drawer before resubmitting.
 *
 * If the backend ever did return a literal `UNCONFIRMED` status, this collapses
 * to the same retired-and-flagged treatment — the safe direction.
 */
export const ROW_STATUS_UNCONFIRMED = 'UNCONFIRMED'

/**
 * True when a row must never be swept into another batch automatically.
 *
 * This — not `isRowAccepted` — is the predicate the submit path, the
 * `submittableRows` collection, `revalidateRow` and the editable callbacks
 * consult, because a row whose outcome is merely unknown is exactly as dangerous
 * to re-POST as one known to be queued.
 *
 * Retired means any of:
 *
 * 1. the server returned a `commandId`. That is positive proof a HeliosCommand
 *    exists for this row, and it outranks the status entirely: a `commandId`
 *    beside a REJECTED status is contradictory server output, and the safe
 *    reading of a contradiction is "the command may exist".
 * 2. the status is `ACCEPTED` (queued) or the `UNCONFIRMED` sentinel; or
 * 3. the status is anything else this UI does not recognise as a proven
 *    rejection. This is the fail-CLOSED rule and it is the whole point of the
 *    predicate being derived rather than a literal comparison: `'QUEUED'`,
 *    `'ACCEPTED_WITH_WARNINGS'`, a differently-cased `'accepted'` or any status
 *    a future backend adds all retire the row instead of leaving it editable and
 *    submittable.
 *
 * Only the literals in `ROW_REJECTION_STATUSES`, with no `commandId`, leave a row
 * un-retired — i.e. editable, and submittable again once the user fixes it.
 *
 * There is deliberately no parallel `retired` boolean on the row: the response
 * fields are the single source of truth, so nothing can drift out of step with
 * them.
 */
export function isRowRetired(row?: ProcessingRowOutcome): boolean {
  if (!row) {
    return false
  }
  if (row.commandId) {
    return true
  }
  if (row.submissionStatus === undefined) {
    return false
  }
  return !isRejectionStatus(row.submissionStatus)
}

/**
 * True when the row was transmitted but its outcome is not a confirmed queueing.
 *
 * Covers the client-side sentinel, a backend-returned literal `UNCONFIRMED`, AND
 * any status this UI does not recognise (see `ROW_REJECTION_STATUSES`) — all
 * three are "retired, but we cannot claim it was queued", which is what the
 * footer, the status pill and the toast must say so the user checks the Commands
 * drawer instead of assuming success or assuming a clean failure.
 */
export function isRowUnconfirmed(row?: ProcessingRowOutcome): boolean {
  return isRowRetired(row) && !isRowAccepted(row)
}

/** Values the row-editing surface holds. ag-grid inline editors yield strings. */
export type ProcessingRowValues = Partial<Record<ProcessingRowField, string>>

/** One grid row = one command. All editable values are held as strings. */
export interface ProcessingGridRow extends Record<ProcessingRowField, string> {
  /** Client-generated, stable for the life of the row, == ag-grid row node id. */
  rowId: string
  validityStatus: ProcessingValidityStatus
  /** field -> messages, consumed by the lib-local error tooltip. */
  errorMessageMap: Map<string, string[]>
  /** Populated from the batch response; undefined until the row is submitted. */
  submissionStatus?: string
  submissionMessages?: string[]
  commandId?: string
  /**
   * Checkbox flags. Real booleans (not strings) because
   * `EditableCheckboxRenderer` writes a boolean through `node.setDataValue`.
   * Both are initialised to `false` in `createRow`; optional here so the type
   * tolerates a row object built without them (e.g. a spec fixture), which is
   * why `toProcessingRequestRow` coerces with `?? false` rather than passing
   * `undefined` through to the wire body.
   */
  ignoreCostBasisAdjustments?: boolean
  forceRealizeAll?: boolean
}

/**
 * The Processing grid's state, hoisted out of the component so it survives the
 * component being destroyed (PVAL-26303 #2).
 *
 * The panel template sits inside `hs-lazy-load-panel`, whose
 * `*ngIf="isPanelOnActiveTab$ | async"` tears it down whenever the panel is not
 * the active tab in its GoldenLayout stack, taking the nav and every
 * component-local field with it. A panel close does the same. The inner
 * `[destroyOnHide]` latch governs the panel's own three top tabs only and
 * reaches neither case.
 *
 * Rows that already became a command (`isRowQueued`) are dropped on restore:
 * users keep one Helios instance open for days, so a grid that accumulates
 * submitted rows is noise. Rejected rows come back editable; UNCONFIRMED rows
 * come back retired, never having been confirmed as queued.
 *
 * In memory for one browser session, so a reload starts empty and nothing is
 * persisted. `rows` and `cellLevelErrorMessageMap` are held BY REFERENCE: cell
 * edits mutate rows in place without replacing the array, so a stored reference
 * captures every keystroke and only a row-SET mutation needs a re-store.
 */
export interface RetainedProcessingGridState {
  /**
   * The command type these rows were entered under. Row shape, validation and
   * the paste contract are all single-command-type, so a mismatch declines
   * rather than painting values into columns that no longer exist.
   */
  commandType: string
  rows: ProcessingGridRow[]
  /** `${rowId}-${field}` -> messages; the live map, not a copy. */
  cellLevelErrorMessageMap: Map<string, string[]>
  /**
   * Rows transmitted by a submit whose response has not landed yet. The live
   * set, for the same reason `rows` is: a submit mutates no row set, so nothing
   * else would write it.
   *
   * A restore retires these `UNCONFIRMED`. The POST is `toPromise()`d with no
   * `takeUntil(destroyed$)`, so once the component dies nobody learns whether it
   * landed, and handing the rows back submittable double-POSTs the commands.
   */
  submittedRowIds: Set<string>
}

/** A single row of the batch POST body. */
export interface CorporateActionProcessingRequestRow {
  rowId: string
  commandType: string
  accountId: number
  exDate: string
  targetCusip: string
  cashPerShare: number
  cashReceived: number
  destCusip: string
  destShareExchRate: number
  destCusipMarketPrice: number
  /** Omitted entirely when Dest CUSIP 2 is blank (`String? = null` server-side). */
  destCusip2?: string
  destShareExchRate2: number
  destCusipMarketPrice2: number
  notes: string
  /**
   * Cost-basis / realization flags. `Boolean = false` on the shipped DTO, but
   * always sent explicitly so the wire body never depends on a Kotlin default.
   * `ignoreCostBasisAdjustments` is what lets perseus accept a cost-basis-adjusted
   * target instead of refusing the command asynchronously; `forceRealizeAll`
   * mirrors perseus-worker's `ProcessCashStockAcquisitionCommand` field of the
   * same name, whose stored proc also defaults it to `false`.
   *
   * Both are editable checkbox columns (see
   * `CASH_STOCK_ACQUISITION_FLAG_COLUMNS`), and both are coerced from the row with
   * an explicit `?? false` so an unticked or fixture-built row still sends a real
   * boolean rather than an absent key.
   */
  ignoreCostBasisAdjustments: boolean
  forceRealizeAll: boolean
}

export interface CorporateActionsProcessingBatchRequest {
  rows: CorporateActionProcessingRequestRow[]
}

/**
 * Per-row result.
 *
 * `status` is intentionally typed as `string` rather than a closed union, because
 * the backend enum is open to additions and an unknown member must NOT be
 * silently treated as a success. The consequence is that the handling has to pick
 * a direction for an unrecognised value, and it fails CLOSED: only the literals
 * in `ROW_REJECTION_STATUSES` (with no `commandId`) leave the row editable and
 * submittable; see `isRowRetired`.
 *
 * `commandId` is not merely display data either — its presence retires the row on
 * its own, because it is positive proof a HeliosCommand exists.
 */
export interface CorporateActionProcessingRowResult {
  rowId: string
  status: string
  messages?: string[]
  commandId?: string | null
}

export interface CorporateActionsProcessingBatchResponse {
  results: CorporateActionProcessingRowResult[]
}
